import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { createEpic } from 'shared/services/epicService';
import { EpicStatus } from 'shared/types/epic';
import { UserSelection } from 'shared/components/userSelection/userSelection';
import { ClerkUser } from 'shared/types/user';
import * as Sentry from '@sentry/react';
import { DatePickerInput } from 'shared/components/DatePickerInput/DatePickerInput';

interface CreateEpicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateEpicModal: React.FC<CreateEpicModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { session } = useSession();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<EpicStatus>(EpicStatus.PLANNING);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOwner, setSelectedOwner] = useState<ClerkUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Titel is verplicht');
      return;
    }

    // Validate dates if both are provided
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        toast.error('Einddatum moet na startdatum liggen');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      await createEpic(
        {
          title: title.trim(),
          description: description.trim() || undefined,
          status,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          ownerId: selectedOwner?.id?.toString() || undefined as string | undefined
        },
        session
      );

      toast.success('Epic succesvol aangemaakt!');
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating epic:', error);
      Sentry.captureException(error, {
        tags: { component: 'CreateEpicModal' },
        extra: { title, status }
      });
      toast.error(
        `Fout bij aanmaken epic: ${error.message || 'Onbekende fout'}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setStatus(EpicStatus.PLANNING);
    setStartDate('');
    setEndDate('');
    setSelectedOwner(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nieuwe Epic Aanmaken</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Titel <span className="text-red-600">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bijv: Q1 2025 Performance Verbetering"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Geef een uitgebreide beschrijving van deze epic..."
              rows={4}
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as EpicStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EpicStatus.PLANNING}>
                  📋 Planning
                </SelectItem>
                <SelectItem value={EpicStatus.IN_PROGRESS}>
                  🚀 In behandeling
                </SelectItem>
                <SelectItem value={EpicStatus.COMPLETED}>
                  ✅ Afgerond
                </SelectItem>
                <SelectItem value={EpicStatus.ON_HOLD}>
                  ⏸️ On hold
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Owner */}
          <div className="space-y-2">
            <Label htmlFor="owner">Eigenaar</Label>
            <div className="flex items-center gap-2 p-2 border rounded-md">
              <UserSelection
                defaultUser={selectedOwner}
                onUserSelect={setSelectedOwner}
              />
              {selectedOwner && (
                <span className="text-sm text-neutral-600">
                  {selectedOwner.first_name} {selectedOwner.last_name}
                </span>
              )}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Startdatum</Label>
              <DatePickerInput
                id="startDate"
                value={startDate}
                onChange={setStartDate}
                placeholder="Kies startdatum"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">Einddatum</Label>
              <DatePickerInput
                id="endDate"
                value={endDate}
                onChange={setEndDate}
                placeholder="Kies einddatum"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Bezig met aanmaken...' : 'Aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
